import axios from "axios";
// config
import { HOST_API } from "src/config-global";
//redux for access
import { setReadAccess, setWriteAccess } from "src/redux/slices/access";
// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API });

axiosInstance.interceptors.request.use(
  function (config) {
    // For FormData bodies, do NOT set Content-Type manually. The browser
    // (XHR) needs to generate `multipart/form-data; boundary=<uuid>` where
    // the boundary matches the one it inserts into the body. If we pin
    // Content-Type to just "multipart/form-data" here, the boundary is
    // missing — multer can't parse the parts, body-parser buffers the raw
    // bytes against its 10 MB JSON limit, and any real upload above about
    // 1.5 MB dies with 413. (Root cause of the reported audition-samples
    // upload bug.) Deleting the header is the canonical fix — axios also
    // lets XHR set it, which is what we want.
    if (config.data instanceof FormData) {
      // Use AxiosHeaders.delete() — the JS `delete` operator does an exact-key
      // match and silently fails when the header is stored under a different
      // casing variant in axios v1.x's AxiosHeaders. The .delete() method
      // uses normalizeHeader() internally so the removal is always applied.
      config.headers.delete('Content-Type');
    } else if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const data = (error.response && error.response.data) || "Something went wrong";
    const status = error.response?.status;
    if (data && typeof data === 'object') {
      return Promise.reject({ ...data, _status: status });
    }
    return Promise.reject(data);
  }
);

export const axiosError = (dispatch, error, dispatchFunct)=>{
    const {read_access, write_access} = error || undefined;
    if(read_access!= undefined && read_access == false){
      dispatch(setReadAccess(false));
    }
    if(write_access != undefined && write_access == false){
      dispatch(setWriteAccess(false));
    }
    dispatchFunct.map(func=>{
      dispatch(func(error))
    })
}

export default axiosInstance;

export const API_ENDPOINTS = {
  geo:{
    country:"/api/geo/countries",
    countryCodes:"/api/geo/country-codes",
    state: (countryId) => `/api/geo/states/${countryId}`,
    city: (stateId) => `/api/geo/cities/${stateId}`,
    search:"/api/geo/search"
  },
  auth: {
    me: "/api/auth/me",
    login: "/api/crm/login",
    register: "/api/crm/register",
    update: "api/crm/account/update",
    companyContext: "/api/crm/company-context",
    userCompanies: "/api/crm/user/companies",
    // Returns { token } — a Firebase custom token whose uid equals the
    // app user's id. Used to sign into Firebase Auth so RTDB rules
    // (auth.uid === $uid) pass for the presence node.
    firebaseToken: "/api/auth/firebase-token",
  },
  agent: {
    autostart: "/api/agent/sessions/autostart",
    sessions: "/api/agent/sessions",
    session: (id) => `/api/agent/sessions/${id}`,
    messages: (id) => `/api/agent/sessions/${id}/messages`,
    confirm: (id) => `/api/agent/sessions/${id}/confirm`,
    confirmation: (id) => `/api/agent/sessions/${id}/confirmation`,
  },
  user: {
    list: "/api/crm/user/list",
    details: "/api/crm/user/details",
    add: "/api/crm/user/add",
    edit: "/api/crm/user/edit",
    invite: "/api/crm/user/invite",
    invitations: "/api/crm/user/invitations",
    resendInvitation: (invitationId) => `/api/crm/user/invitations/${invitationId}/resend`,
    sendSetPassword: (id) => `/api/crm/user/${id}/send-set-password-link`,
  },
  account:{
    general:"/api/profile",
    workspace:{
      get:"/api/profile/my-organization-profile",
      update:"/api/profile/organization",
      stamps:"/api/profile/organization/stamps"
    },
    bank_account:"/api/profile/bank-account",
    bank_account_primary:"/api/profile/bank-account/primary",
    change_password: "/api/profile/password",
    notifications: "/api/profile/notification-preferences",
    update_notifications: "/api/profile/notification-preferences"
  },
  role: {
   list: "/api/crm/user/roles",
   permissions: "/api/crm/user/role-permissions",
   add: "/api/crm/user/add-role",
   edit: "/api/crm/user/edit-role",
  },
  rolePermissions : "/api/crm/user/role-permissions",
  checkpermission: "/api/crm/user/check-permissions",
  admin: {
    dashboard: "/api/crm/dashboard/stats"
  },
  wallet: {
    balance: "/api/wallet/balance",
    transactions: "/api/wallet/transactions",
    transactionDetails: (id) => `/api/wallet/transactions/${id}`,
  },
  expenses: {
    categories: "/api/expenses/categories",
    projectCostCenters: "/api/expenses/project-cost-centers",
    list: "/api/expenses",
    create: "/api/expenses",
    update: (id) => `/api/expenses/${id}`,
    delete: (id) => `/api/expenses/${id}`,
    claim: "/api/expenses/claim",
    claims: "/api/expenses/claims",
    claimDetails: (id) => `/api/expenses/claims/${id}`,
    claimsReview: "/api/expenses/claims/review",
    approveClaim: (id) => `/api/expenses/claims/${id}/approve`,
    rejectClaim: (id) => `/api/expenses/claims/${id}/reject`,
    markClaimPaid: (id) => `/api/expenses/claims/${id}/mark-paid`,
  },
  jobs:{
      list:"/api/jobs/my-posts",
      applicants:"/api/jobs/my-posts/{id}/applicants",
      create:"/api/jobs",
      roles:(id) =>`/api/jobs/${id}/roles`,
      get:(id) =>`/api/jobs/my-posts/${id}`,
      delete:(id) =>`/api/jobs/${id}`,
      uploadImages:(id) =>`/api/jobs/${id}/upload-images`,
      invite:(id) =>`/api/jobs/${id}/invite`,
  },
  // Generic invitations read, shared by Job and Audition "Invitees" tabs.
  invitations: {
    list: (inviteableType, inviteableId, page = 1, limit = 20) =>
      `/api/invitations?inviteableType=${inviteableType}&inviteableId=${inviteableId}&page=${page}&limit=${limit}`,
  },
  proposals: {
      list: "/api/proposals",
      create: (category) => `/api/proposals/${category}`,
      update: (category, id) => `/api/proposals/${category}/${id}`,
      delete: (category, id) => `/api/proposals/${category}/${id}`,
      updateStatus: (category, id) => `/api/proposals/${category}/${id}/status`,
      setVisibility: (category, id) => `/api/proposals/${category}/${id}/visibility`,
      detail: (category, id) => `/api/proposals/${category}/${id}`,
      uploadMedia: (category, id) => `/api/proposals/${category}/${id}/media`,
      uploadVideos: (category, id) => `/api/proposals/${category}/${id}/videos`,
      uploadDocumentLinks: (category, id) => `/api/proposals/${category}/${id}/document-links`,
      deleteMedia: (mediaId) => `/api/proposals/media/${mediaId}`,
      linkDocuments: (category, id) => `/api/proposals/${category}/${id}/link-documents`,
      linkCommentAttachment: (category, id) => `/api/proposals/${category}/${id}/link-comment-attachment`,
      tags: "/api/proposals/tags",
      folders: "/api/proposals/folders",
      folderUpdate: (id) => `/api/proposals/folders/${id}`,
      folderDelete: (id) => `/api/proposals/folders/${id}`,
      folderTree: '/api/proposals/folders/tree',
      folderCopy: (id) => `/api/proposals/folders/${id}/copy`,
      folderCopySelective: (id) => `/api/proposals/folders/${id}/copy-selective`,
      folderImportInto: (sourceId, targetId) => `/api/proposals/folders/${sourceId}/import-into/${targetId}`,
      folderDocuments: (id) => `/api/proposals/folders/${id}/documents`,
      folderDetails: (id) => `/api/proposals/folders/${id}/details`,
    folderPitch: (id) => `/api/proposals/folders/${id}/pitch`,
      sections: (proposalId) => `/api/proposals/${proposalId}/sections`,
      createSection: (proposalId) => `/api/proposals/${proposalId}/sections`,
      updateSection: (proposalId, id) => `/api/proposals/${proposalId}/sections/${id}`,
      deleteSection: (proposalId, id) => `/api/proposals/${proposalId}/sections/${id}`,
      discussion: (folderId) => `/api/proposals/folders/${folderId}/discussion`,
      deleteDiscussionMessage: (folderId, messageId) => `/api/proposals/folders/${folderId}/discussion/${messageId}`,
      chatConversation: (folderId) => `/api/proposals/folders/${folderId}/chat`,
      chatSend: (folderId) => `/api/proposals/folders/${folderId}/chat/send`,
      chatMarkAsRead: (folderId) => `/api/proposals/folders/${folderId}/chat/read`,
      sendToClient: (id) => `/api/proposals/folders/${id}/send-to-client`,
      notifyResources: (folderId) => `/api/proposals/folders/${folderId}/notify-resources`,
      whatsappTemplates: "/api/whatsapp/templates",
      generateShareToken: (id) => `/api/proposals/folders/${id}/share`,
      publicView: (shareToken) => `/api/proposals/public/${shareToken}`,
      accessRequests: "/api/proposals/access-requests",
      acceptAccessRequest: (token) => `/api/proposals/access-requests/${token}/accept`,
      rejectAccessRequest: (token) => `/api/proposals/access-requests/${token}/reject`,
      deleteAccessRequest: (token) => `/api/proposals/access-requests/${token}`,
      toggleAllowEdits: (token) => `/api/proposals/access-requests/${token}/allow-edits`,
      estimate: (proposalId) => `/api/proposals/estimate/${proposalId}`,
      estimateSection: (proposalId, sectionId) => `/api/proposals/estimate/${proposalId}/sections/${sectionId}`,
      estimateItem: (proposalId, sectionId, itemId) => `/api/proposals/estimate/${proposalId}/sections/${sectionId}/items/${itemId}`,
      terms: (proposalId) => `/api/proposals/terms/${proposalId}`,
      termsSection: (proposalId, sectionId) => `/api/proposals/terms/${proposalId}/sections/${sectionId}`,
  },
  locations: {
      list: "/api/locations",
      types: "/api/locations/types",
      create: "/api/locations",
      update: (id) => `/api/locations/${id}`,
      delete: (id) => `/api/locations/${id}`,
      detail: (id) => `/api/locations/${id}`,
      uploadAreaMedia: (locationId, areaId) => `/api/locations/${locationId}/areas/${areaId}/media`,
      bookings: "/api/locations/bookings",
      updateBookingStatus: (id) => `/api/locations/bookings/${id}/status`,
      cancelBooking: (id) => `/api/locations/bookings/${id}`,
      schedule: "/api/locations/schedule",
      blockedDates: "/api/locations/blocked-dates",
      availability: "/api/locations/availability",
  },
  equipments: {
      list: "/api/equipments",
      categories: "/api/equipments/categories",
      create: "/api/equipments",
      update: (id) => `/api/equipments/${id}`,
      delete: (id) => `/api/equipments/${id}`,
      detail: (id) => `/api/equipments/${id}`,
      uploadMedia: (id) => `/api/equipments/${id}/media`,
      bookings: "/api/equipments/bookings",
      updateBookingStatus: (id) => `/api/equipments/bookings/${id}/status`,
      cancelBooking: (id) => `/api/equipments/bookings/${id}`,
      schedule: "/api/equipments/schedule",
      blockedDates: "/api/equipments/blocked-dates",
      availability: "/api/equipments/availability",
  },
  locationAttributes: {
      amenities: "/api/location-attributes/amenities",
      inclusions: "/api/location-attributes/inclusions",
      exclusions: "/api/location-attributes/exclusions",
      createAmenity: "/api/location-attributes/amenities",
      createInclusion: "/api/location-attributes/inclusions",
      createExclusion: "/api/location-attributes/exclusions",
  },
  talent:{
    list:"/api/talent-managers/talents",
    searchGlobal:"/api/talent-managers/talents/search-global",
    types:"/api/talents/types",
    edit:(id) =>`/api/talent-managers/talents/${id}`,
    delete:(id) =>`/api/talent-managers/talents/${id}`,
    mediaUpdate:(id) =>`/api/talent-managers/talents/${id}/media`,
    mediaReorder:(id) =>`/api/talent-managers/talents/${id}/media/reorder`,
    mediaPolaroid:(id, mediaId) =>`/api/talent-managers/talents/${id}/media/${mediaId}/polaroid`,
    hired:"/api/talent-managers/hired-talents",
    hiredSchedules:"/api/talent-managers/hired-talent-schedules",
    myAvailability:"/api/talent-managers/my-talents-availability",
    profile:(short_url) =>`/api/talents/${short_url}`,
    languages:"/api/languages",
    passport: (id) => `/api/talents/${id}/passport`,
    archetypes: "/api/archetypes",
    archetype: (n) => `/api/archetypes/${n}`,
    portfolio: (id) => `/api/talents/${id}/portfolio`,
    portfolioRawMedia: (id) => `/api/talents/${id}/portfolio/raw-media`,
    portfolioReorder: (id) => `/api/talents/${id}/portfolio/reorder`,
    portfolioMedia: (id, mediaId) => `/api/talents/${id}/portfolio/media/${mediaId}`,
    portfolioVisibility: (id) => `/api/talents/${id}/portfolio/visibility`,
    portfolioItem: (id, itemId) => `/api/talents/${id}/portfolio/${itemId}`,
  },
  tours: {
    list: '/api/tours',
    create: '/api/tours',
    detail: (id) => `/api/tours/${id}`,
    update: (id) => `/api/tours/${id}`,
    availability: (id) => `/api/tours/${id}/availability`,
    travelLegs: (id) => `/api/tours/${id}/travel-legs`,
    travelLeg: (id, legId) => `/api/tours/${id}/travel-legs/${legId}`,
    accommodations: (id) => `/api/tours/${id}/accommodations`,
    accommodation: (id, accommodationId) => `/api/tours/${id}/accommodations/${accommodationId}`,
    complianceDocuments: (id) => `/api/tours/${id}/compliance-documents`,
    complianceDocument: (id, documentId) => `/api/tours/${id}/compliance-documents/${documentId}`,
  },
  applications:{
    list:"/api/jobs/my-applications",
    applied:"/api/jobs/my-applications",
    received:"/api/jobs/my-job-applications",
    updateStatus:(jobId, id) => `/api/jobs/${jobId}/applications/${id}/status`
  },
  client:{
    list:"/api/clients",
    industries:"/api/clients/industries",
    edit:(id) =>`/api/clients/${id}`,
    company:(id) =>`/api/clients/${id}/company`,
    update:"/api/clients/update",
    profile: "/api/clients/profile",
    poc: (id) =>`/api/clients/${id}/poc`,
    updatePoc: (contactId) =>`/api/clients/poc/${contactId}`,
    deletePoc: (contactId) =>`/api/clients/poc/${contactId}`,
    giveCrmAccess: (contactId) =>`/api/clients/poc/${contactId}/give-crm-access`,
    revokeCrmAccess: (contactId) =>`/api/clients/poc/${contactId}/revoke-crm-access`,
    addContactByCompany: (companyId) =>`/api/companies/${companyId}/contacts`,
    billing: (id) =>`/api/clients/${id}/billing`,
    newClient: "/api/clients/",
    newClientCompany: "/api/clients/company",
    search:"/api/clients/address/search",
    delete: (id) =>`/api/clients/${id}`
  },
  companies:{
    list:"/api/clients",
    search:"/api/companies/search",
    agencies:"/api/companies/agencies",
    create:"/api/companies"
  },
  invoice: {
    list: "/api/billing",
    create: "/api/billing",
    edit: (id) => `/api/billing/${id}`,
    details: (id) => `/api/billing/${id}`,
    delete: (id) => `/api/billing/${id}`,
    // Same path as list/create — every billing endpoint takes a `?type=`
    // query param to discriminate `invoice` vs `performa`. Use this alias
    // when the call site cares about that discrimination.
    byType: "/api/billing",
    // Returns { types, statuses, transitions } so the UI can offer only
    // the valid next statuses for a given current status.
    meta: "/api/billing/meta",
    // PATCH a single status transition. Body: { status }. On 4xx the
    // server returns { allowedNextStatuses: [...] } alongside the error
    // message — surface those to the user when the local transition map
    // is out of sync.
    updateStatus: (id) => `/api/billing/${id}/status`,
    email: (id) => `/api/billing/${id}/email`,
    convert: (id) => `/api/billing/${id}/convert`,
    // Generate a public accept/reject link for a proforma. Auth-required.
    shareToken: "/api/billing/share-token",
    // Public acceptance endpoint — no auth.
    // GET fetches the proforma preview, POST records the decision.
    respond: (token) => `/api/billing/respond/${token}`,
    // Removes the background from an already-uploaded image (e.g. a raw
    // seal/signature upload). Body: { imageUrl }. Does not persist
    // anything to Company/Invoice — that happens separately once the user
    // picks original vs. cleaned (see account.workspace.stamps).
    removeBg: "/api/billing/remove-bg",
    // Streams an S3 image through our own API — used for images embedded
    // in the react-pdf-rendered PDF (@react-pdf/renderer's <Image> does a
    // real fetch(), unlike a passive <img> tag, so it's subject to the S3
    // bucket's CORS policy). Public, no auth (see server route comment).
    imageProxy: (url) => `${HOST_API.replace(/\/$/, '')}/api/billing/image-proxy?url=${encodeURIComponent(url)}`,
  },
  reports: {
    clientRevenue: "/api/financial-reports/client-revenue",
    talentEarnings: "/api/financial-reports/talent-earnings",
    agencyShares: "/api/financial-reports/agency-shares",
    // Grouped under the same /api/financial-reports namespace as its three
    // siblings above — NOT the bare /api/expenses path, which already
    // belongs to the unrelated personal-expenses list (user-expenses-travel-view.js,
    // old {expenses,total} envelope, no `success` field). This report uses
    // the newer {success, data} envelope, so it can't be the same route.
    expenses: "/api/financial-reports/expenses",
  },
  settlements: {
    list: "/api/settlements",
    generate: "/api/settlements/generate",
    detail: (id) => `/api/settlements/${id}`,
    dispute: (id) => `/api/settlements/${id}/dispute`,
    resolveDispute: (id) => `/api/settlements/${id}/resolve-dispute`,
    approve: (id) => `/api/settlements/${id}/approve`,
    pay: (id) => `/api/settlements/${id}/pay`,
  },
  talentSubcontracts: {
    list: "/api/talent-subcontracts",
    create: "/api/talent-subcontracts",
    detail: (id) => `/api/talent-subcontracts/${id}`,
    activate: (id) => `/api/talent-subcontracts/${id}/activate`,
    terminate: (id) => `/api/talent-subcontracts/${id}/terminate`,
  },
  services:{
    list:"/api/service-master",
    search:"/api/service-master/search",
    currencyList:"/api/service-master/currencies",
    serviceMasterTax:(countryId) => `/api/service-master-tax/country/${countryId}`
  },
  projects:{
    list:"/api/projects",
    create:"/api/projects",
    edit:(id) => `/api/projects/${id}`,
    details:(id) => `/api/projects/${id}`,
    delete:(id) => `/api/projects/${id}`,
    search:"/api/projects/search",
    kanban:(projectId) => `/api/projects/${projectId}/kanban`,
    kabanEdit:(taskId) => `/api/projects/tasks/${taskId}/position`,
    tasks:(projectId) => `/api/projects/${projectId}/tasks`
  },
  // FILM MODULE — a film is a Project (projectId). See docs/FILM-MODULE-API.md
  film: {
    scripts: {
      list: "/api/scripts",                                  // ?projectId=
      create: "/api/scripts",
      generate: "/api/scripts/generate",                     // AI: prompt + rough → screenplay
      tree: (id) => `/api/scripts/${id}`,                    // full scene + element tree
      createScene: (id) => `/api/scripts/${id}/scenes`,
      lock: (id) => `/api/scripts/${id}/lock`,
      unlock: (id) => `/api/scripts/${id}/unlock`,
      updateScene: (sceneId) => `/api/scripts/scenes/${sceneId}`,
      saveElements: (sceneId) => `/api/scripts/scenes/${sceneId}/elements`,
      // Scene assignments (polymorphic SceneLink layer)
      assignable: (projectId) => `/api/scripts/projects/${projectId}/assignable`,
      quickAddMaster: (projectId, pool) => `/api/scripts/projects/${projectId}/master/${pool}`,
      sceneAssignments: (sceneId) => `/api/scripts/scenes/${sceneId}/assignments`,
      updateAssignment: (assignmentId) => `/api/scripts/scene-assignments/${assignmentId}`,
      deleteAssignment: (assignmentId) => `/api/scripts/scene-assignments/${assignmentId}`,
    },
    breakdown: {
      start: (scriptId) => `/api/breakdown/scripts/${scriptId}`,
      job: (jobId) => `/api/breakdown/jobs/${jobId}`,
      suggestions: (scriptId) => `/api/breakdown/scripts/${scriptId}/suggestions`,
      confirmCharacter: (id) => `/api/breakdown/characters/${id}/confirm`,
      rejectCharacter: (id) => `/api/breakdown/characters/${id}`,
    },
    assets: {
      list: "/api/production-assets",                        // ?projectId=&category=&status=
      create: "/api/production-assets",
      update: (id) => `/api/production-assets/${id}`,
      remove: (id) => `/api/production-assets/${id}`,
    },
    schedule: {
      optimize: "/api/schedule/optimize",
      days: "/api/schedule/days",                            // ?projectId=
      createDay: "/api/schedule/days",
      updateDay: (id) => `/api/schedule/days/${id}`,
      deleteDay: (id) => `/api/schedule/days/${id}`,
      setDayScenes: (id) => `/api/schedule/days/${id}/scenes`,
      report: (id) => `/api/schedule/days/${id}/report`,
    },
    callSheets: {
      list: "/api/call-sheets",                              // ?projectId=
      generate: "/api/call-sheets/generate",
    },
  },
  projectTemplates:{
    list:"/api/project-templates",
    detail:(id) => `/api/project-templates/${id}`,
    create:"/api/project-templates",
    update:(id) => `/api/project-templates/${id}`,
    delete:(id) => `/api/project-templates/${id}`,
  },
  messages: {
    send: "/api/messages/send",
    chats: "/api/messages/chats",
    history: (otherUserId) => `/api/messages/history/${otherUserId}`,
    userList: "/api/messages/user-list",
    unreadCount: "/api/messages/unread/count",
    markAsRead: (senderId) => `/api/messages/read/${senderId}`,
    searchUsers: "/api/messages/search-users",
    upload: "/api/messages/upload",
    syncProjectChat: (projectId) => `/api/messages/sync-project-chat/${projectId}`,
    groupSend: "/api/messages/group/send",
    groupHistory: (chatId) => `/api/messages/group/history/${chatId}`,
    groupConversation: (projectId) => `/api/messages/group/conversation/${projectId}`,
    reactions: (messageId) => `/api/messages/${messageId}/reactions`,
    delete: (messageId) => `/api/messages/${messageId}`,
    removeParticipant: (chatId, userId) => `/api/messages/${chatId}/participants/${userId}`,
  },
  calendar: {
    events: "/api/calendar/events",
    event: (id) => `/api/calendar/events/${id}`,
    view: "/api/calendar/calendar",
    upcoming: "/api/calendar/events/upcoming",
    stats: "/api/calendar/stats",
  },
  talentBookings: {
    create: "/api/talent-bookings",
    mySent: "/api/talent-bookings/sent",
    received: "/api/talent-bookings/received",
    forTalent: (talentId) => `/api/talent-bookings/${talentId}`,
    schedule: (talentId) => `/api/talent-bookings/${talentId}/schedule`,
    updateStatus: (bookingId) => `/api/talent-bookings/${bookingId}/status`,
    availability: "/api/talent-bookings/availability",
    updateDetails: (id) => `/api/talent-bookings/${id}/details`,
    cancel: (bookingId) => `/api/talent-bookings/${bookingId}`,
  },
  talentOffers: {
    list: "/api/talent-offers",
    received: "/api/talent-offers/managed-talents",
    create: "/api/talent-offers",
    candidates: "/api/talent-offers/candidates",
    details: (id) => `/api/talent-offers/${id}`,
    update: (id) => `/api/talent-offers/${id}`,
    messages: (id) => `/api/talent-offers/${id}/messages`,
    send: (id) => `/api/talent-offers/${id}/send`,
    counter: (id) => `/api/talent-offers/${id}/counter`,
    negotiate: (id) => `/api/talent-offers/${id}/negotiate`,
    withdraw: (id) => `/api/talent-offers/${id}/withdraw`,
    accept: (id) => `/api/talent-offers/${id}/accept`,
    reject: (id) => `/api/talent-offers/${id}/reject`,
    // Only fires on status:'accepted'. Idempotent — re-calling on an offer
    // that already has one returns it (data.alreadyExisted: true) rather
    // than creating a duplicate.
    createContract: (id) => `/api/talent-offers/${id}/create-contract`,
    bookings: "/api/talent-offers/bookings",
    booking: (id) => `/api/talent-offers/bookings/${id}`,
    signBooking: (id) => `/api/talent-offers/bookings/${id}/sign`,
    orders: "/api/talent-offers/orders",
    order: (id) => `/api/talent-offers/orders/${id}`,
  },
  tasks: {
    list: (projectId) => `/api/tasks?projectId=${projectId}`,
    myCalendarTasks: "/api/tasks/my-calendar-tasks",
    create: "/api/tasks",
    update: (id) => `/api/tasks/${id}`,
    move: (id) => `/api/tasks/${id}/move`,
    delete: (id) => `/api/tasks/${id}`,
    assignees: (id) => `/api/tasks/${id}/assignees`,
    companyUsers: (projectId) => `/api/tasks/company-users?projectId=${projectId}`,
    resources: {
      list: (taskId) => `/api/tasks/${taskId}/resources`,
      add: (taskId) => `/api/tasks/${taskId}/resources`,
      remove: (taskId, id) => `/api/tasks/${taskId}/resources/${id}`,
    },
    subtasks: {
      list: (taskId) => `/api/tasks/${taskId}/subtasks`,
      assignees: (taskId) => `/api/tasks/${taskId}/subtasks/assignees`,
      eligibleAssignees: (taskId, subtaskId) => `/api/tasks/${taskId}/subtasks/${subtaskId}/eligible-assignees`,
      addAssignees: (taskId, subtaskId) => `/api/tasks/${taskId}/subtasks/${subtaskId}/assignees`,
      create: (taskId) => `/api/tasks/${taskId}/subtasks`,
      update: (taskId, id) => `/api/tasks/${taskId}/subtasks/${id}`,
      delete: (taskId, id) => `/api/tasks/${taskId}/subtasks/${id}`,
    },
    comments: {
      list: (taskId) => `/api/tasks/${taskId}/comments`,
      create: (taskId) => `/api/tasks/${taskId}/comments`,
      update: (taskId, id) => `/api/tasks/${taskId}/comments/${id}`,
      delete: (taskId, id) => `/api/tasks/${taskId}/comments/${id}`,
      mentionableUsers: (taskId) => `/api/tasks/${taskId}/mentionable-users`,
    },
    attachments: {
      list: (taskId) => `/api/tasks/${taskId}/attachments`,
      create: (taskId) => `/api/tasks/${taskId}/attachments`,
      delete: (taskId, id) => `/api/tasks/${taskId}/attachments/${id}`,
    },
    get: (id) => `/api/tasks/${id}`,
  },
  payroll: {
    list: "/api/payroll",
    byProject: (projectId) => `/api/payroll/project/${projectId}`,
    create: "/api/payroll",
    update: (id) => `/api/payroll/${id}`,
    delete: (id) => `/api/payroll/${id}`,
    details: (id) => `/api/payroll/${id}`,
    frequencies: "/api/payroll/frequencies",
    seedFrequencies: "/api/payroll/frequencies/seed",
  },
  employeeTypes: {
    list: "/api/employee-types",
    seed: "/api/employee-types/seed",
    create: "/api/employee-types",
    update: (id) => `/api/employee-types/${id}`,
    delete: (id) => `/api/employee-types/${id}`,
  },
  attendance: {
    byProject: (projectId) => `/api/attendance/project/${projectId}`,
    userHistory: (projectId) => `/api/attendance/project/${projectId}/user-history`,
    summary: (projectId) => `/api/attendance/project/${projectId}/summary`,
    payoutQueue: (projectId) => `/api/attendance/project/${projectId}/payout-queue`,
    updatePayoutStatus: (id) => `/api/attendance/payout/${id}/status`,
    updatePayoutNote: (id) => `/api/attendance/payout/${id}/notes`,
    freelancerPaymentLedger: "/api/attendance/freelancer/payment-ledger",
    save: "/api/attendance",
    bulkSave: "/api/attendance/bulk",
    delete: (id) => `/api/attendance/${id}`,
  },
  documentFolders: {
    list: (projectId, parentId, proposalId) => {
      const params = new URLSearchParams();
      if (projectId) params.append('projectId', projectId);
      if (parentId) params.append('parentId', parentId);
      if (proposalId) params.append('proposalId', proposalId);
      return `/api/documents/folders${params.toString() ? `?${params.toString()}` : ''}`;
    },
    create: '/api/documents/folders',
    update: (id) => `/api/documents/folders/${id}`,
    delete: (id) => `/api/documents/folders/${id}`,
    details: (id) => `/api/documents/folders/${id}`,
    files: (id) => `/api/documents/folders/${id}/files`,
    uploadFile: (id) => `/api/documents/folders/${id}/files`,
    proposalFiles: (proposalId) => `/api/documents/proposals/${proposalId}/files`,
    uploadProposalFile: (proposalId) => `/api/documents/proposals/${proposalId}/files`,
    renameFile: (folderId, fileId) => `/api/documents/folders/${folderId}/files/${fileId}`,
    deleteFile: (folderId, fileId) => `/api/documents/folders/${folderId}/files/${fileId}`,
    deleteAnyFile: (fileId) => `/api/documents/files/${fileId}`,
    toggleFavorite: (id) => `/api/documents/folders/${id}/favorite`,
    breadcrumb: (id) => `/api/documents/folders/${id}/breadcrumb`,
    subfolders: (id) => `/api/documents/folders/${id}/subfolders`,
    // Move endpoints
    foldersForMove: (excludeFolderId) => `/api/documents/folders-for-move${excludeFolderId ? `?excludeFolderId=${excludeFolderId}` : ''}`,
    moveFolder: (id) => `/api/documents/folders/${id}/move`,
    moveFile: (folderId, fileId) => `/api/documents/folders/${folderId}/files/${fileId}/move`,
    // Sharing endpoints
    share: (id) => `/api/documents/folders/${id}/share`,
    getShares: (id) => `/api/documents/folders/${id}/shares`,
    removeShare: (id, shareId) => `/api/documents/folders/${id}/shares/${shareId}`,
    generateShareLink: (id) => `/api/documents/folders/${id}/share-link`,
    disableShareLink: (id) => `/api/documents/folders/${id}/share-link`,
    acceptShare: (token) => `/api/documents/accept-share/${token}`,
    // Public shared access
    sharedFolder: (token) => `/api/documents/shared/${token}`,
    sharedFolderFiles: (token) => `/api/documents/shared/${token}/files`,
    // File sharing endpoints
    sharedWithMe: '/api/documents/files/shared-with-me',
    browseFiles: '/api/documents/files/browse',
    shareFile: (fileId) => `/api/documents/files/${fileId}/share`,
    getFileShares: (fileId) => `/api/documents/files/${fileId}/shares`,
    removeFileShare: (fileId, shareId) => `/api/documents/files/${fileId}/shares/${shareId}`,
    acceptFileShare: (token) => `/api/documents/accept-file-share/${token}`,
  },
  incentives: {
    list: "/api/incentives",
    create: "/api/incentives",
    update: "/api/incentives",
    delete: "/api/incentives",
    details: (id) => `/api/incentives/${id}`,
  },
  auditions: {
    list: "/api/auditions",
    create: "/api/auditions",
    update: (id) => `/api/auditions/${id}`,
    delete: (id) => `/api/auditions/${id}`,
    detail: (id) => `/api/auditions/${id}`,
    uploadSamples: (id) => `/api/auditions/${id}/samples`,
    inviteTalents: (id) => `/api/auditions/${id}/invite/talents`,
    sendInvitations: (id) => `/api/auditions/${id}/invite`,
    responsesByJobAndUser: (jobId, userId) => `/api/auditions/responses/job/${jobId}/user/${userId}`,
    updateResponseStatus: (responseId) => `/api/auditions/response/${responseId}/status`,
    myCreated: "/api/auditions/my-created",
    myCreatedDetail: (id) => `/api/auditions/my-created/${id}`,
  },
  myBookings: {
    list: (params = {}) => {
      const query = new URLSearchParams(
        Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
      ).toString();
      return query ? `/api/my-bookings?${query}` : '/api/my-bookings';
    },
    create: '/api/my-bookings',
    detail: (type, id) => `/api/my-bookings/${type}/${id}`,
    update: (type, id) => `/api/my-bookings/${type}/${id}`,
    delete: (type, id) => `/api/my-bookings/${type}/${id}`,
  },
  partners: {
    list: (params = {}) => {
      const query = new URLSearchParams(
        Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
      ).toString();
      return query ? `/api/partners?${query}` : '/api/partners';
    },
    create: '/api/partners',
    detail: (id) => `/api/partners/${id}`,
    update: (id) => `/api/partners/${id}`,
    delete: (id) => `/api/partners/${id}`,
  },
  talentRepresentationAgreements: {
    create: '/api/talent-representation-agreements',
    list: '/api/talent-representation-agreements',
    detail: (id) => `/api/talent-representation-agreements/${id}`,
    update: (id) => `/api/talent-representation-agreements/${id}`,
    activate: (id) => `/api/talent-representation-agreements/${id}/activate`,
    terminate: (id) => `/api/talent-representation-agreements/${id}/terminate`,
  },
  contracts: {
    list: '/api/contracts',
    create: '/api/contracts',
    upload: '/api/contracts/upload',
    detail: (id) => `/api/contracts/${id}`,
    update: (id) => `/api/contracts/${id}`,
    delete: (id) => `/api/contracts/${id}`,
    sendForReview: (id) => `/api/contracts/${id}/send-for-review`,
    sendForSigning: (id) => `/api/contracts/${id}/send-for-signing`,
    execute: (id) => `/api/contracts/${id}/execute`,
    terminate: (id) => `/api/contracts/${id}/terminate`,
    duplicate: (id) => `/api/contracts/${id}/duplicate`,
    signatories: (id) => `/api/contracts/${id}/signatories`,
    deleteSignatory: (id, sigId) => `/api/contracts/${id}/signatories/${sigId}`,
    resendSignatory: (id, sigId) => `/api/contracts/${id}/signatories/${sigId}/resend`,
    versions: (id) => `/api/contracts/${id}/versions`,
    comments: (id) => `/api/contracts/${id}/comments`,
    resolveComment: (id, commentId) => `/api/contracts/${id}/comments/${commentId}/resolve`,
    deleteComment: (id, commentId) => `/api/contracts/${id}/comments/${commentId}`,
    activity: (id) => `/api/contracts/${id}/activity`,
    attachments: (id) => `/api/contracts/${id}/attachments`,
    deleteAttachment: (id, attId) => `/api/contracts/${id}/attachments/${attId}`,
    revenueSplits: (id) => `/api/contracts/${id}/revenue-splits`,
    revenueSplit: (id, splitId) => `/api/contracts/${id}/revenue-splits/${splitId}`,
    expenseCategories: (id) => `/api/contracts/${id}/expense-categories`,
    expenseCategory: (id, categoryId) => `/api/contracts/${id}/expense-categories/${categoryId}`,
    templates: '/api/contracts/templates',
    template: (id) => `/api/contracts/templates/${id}`,
    templateVisibility: (id) => `/api/contracts/templates/${id}/visibility`,
    sign: (token) => `/api/contracts/sign/${token}`,
    signRevenueSplits: (token) => `/api/contracts/sign/${token}/revenue-splits`,
    signExpenseCategories: (token) => `/api/contracts/sign/${token}/expense-categories`,
    review: (token) => `/api/contracts/review/${token}`,
    reviewComments: (token) => `/api/contracts/review/${token}/comments`,
    keyComponents: '/api/contracts/key-components',
    keyComponent: (id) => `/api/contracts/key-components/${id}`,
  },
};
